const SENSITIVE_KEYS = new Set(
  [
    "password",
    "passcode",
    "pin",
    "token",
    "accessToken",
    "refreshToken",
    "idToken",
    "authorization",
    "cookie",
    "set-cookie",
    "apiKey",
    "api_key",
    "secret",
    "clientSecret",
    "privateKey",
    "email",
    "phone",
    "mobile",
    "creditCard",
    "cardNumber",
    "cvv",
  ].map((key) => key.toLowerCase()),
);

const REDACTED = "[REDACTED]";
const MAX_MASK_DEPTH = 20;

function isSensitiveKey(key: string): boolean {
  const normalized = key.toLowerCase().replace(/[-_\s]/g, "");
  for (const sensitive of SENSITIVE_KEYS) {
    if (normalized === sensitive.replace(/[-_\s]/g, "")) return true;
  }
  return false;
}

function maskEmail(value: string): string {
  const match = /^([^@\s]+)@([^@\s]+)$/.exec(value);
  if (!match) return REDACTED;
  const [, local, domain] = match;
  if (!local || !domain) return REDACTED;
  const visible = local.slice(0, 1);
  return `${visible}${"*".repeat(Math.max(local.length - 1, 1))}@${domain}`;
}

function maskPhone(value: string): string {
  const digits = value.replace(/\D/g, "");
  if (digits.length < 4) return REDACTED;
  const lastFour = digits.slice(-4);
  return `${"*".repeat(Math.max(digits.length - 4, 4))}${lastFour}`;
}

function maskValueForKey(key: string, value: unknown): unknown {
  const normalized = key.toLowerCase().replace(/[-_\s]/g, "");
  if (normalized === "email" && typeof value === "string") {
    return maskEmail(value);
  }
  if ((normalized === "phone" || normalized === "mobile") && typeof value === "string") {
    return maskPhone(value);
  }
  return REDACTED;
}

/**
 * Recursively produces a masked deep copy of `value`. The original object
 * graph is never mutated. Circular references are broken with `[Circular]`
 * rather than throwing, and recursion is bounded by `MAX_MASK_DEPTH`.
 */
export function maskSensitiveData(value: unknown, seen: WeakSet<object> = new WeakSet(), depth = 0): unknown {
  if (depth > MAX_MASK_DEPTH) return "[Truncated]";

  if (value === null || typeof value !== "object") {
    return value;
  }

  if (seen.has(value)) {
    return "[Circular]";
  }

  if (Array.isArray(value)) {
    seen.add(value);
    return value.map((item) => maskSensitiveData(item, seen, depth + 1));
  }

  seen.add(value);
  const result: Record<string, unknown> = {};
  for (const [key, val] of Object.entries(value as Record<string, unknown>)) {
    if (isSensitiveKey(key)) {
      result[key] = maskValueForKey(key, val);
    } else if (val !== null && typeof val === "object") {
      result[key] = maskSensitiveData(val, seen, depth + 1);
    } else {
      result[key] = val;
    }
  }
  return result;
}

export function isKeySensitive(key: string): boolean {
  return isSensitiveKey(key);
}
